"use client";

import { useCallback, useEffect, useState } from "react";
import type { GalleryComponent, GalleryImage } from "../../types";
import { useRendererRuntime } from "../../runtime/RendererRuntime";

/**
 * Gallery block — a true PHOTO widget, visually distinct from the clickable
 * card grids (LinkTileGrid, DocumentCard): each photo sits in a neutral
 * rounded frame with a slim figure legend UNDER the image (no tinted caption
 * box), and clicking a photo opens a fullscreen lightbox with zoom,
 * previous/next navigation and keyboard control (Escape, arrows).
 *
 * Used by the article photo strips, the migrated "Gallérie des photos" pages
 * and the billets-de-banque pages. Each image `src` is a bucket-relative
 * storage key (e.g. `images/galleries/bcc_1.png`) resolved through the
 * runtime `assetResolver` into an environment-aware absolute URL —
 * already-absolute URLs pass through unchanged.
 */

/** Maps an explicit column count to a Tailwind grid-columns class. */
function columnsClass(columns?: number): string {
  switch (columns) {
    case 1:
      return "grid-cols-1";
    case 2:
      return "grid-cols-1 sm:grid-cols-2";
    case 4:
      return "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4";
    case 3:
    default:
      // Default responsive layout when no explicit count is given.
      return "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3";
  }
}

type ResolvedImage = { src: string; caption: string };

/** Fullscreen zoom overlay (position:fixed + zIndex inline — no portal, so the
 * SDK never imports react-dom, which the admin's jest cannot resolve from the
 * package dist). The server never renders it: it only opens from a click. */
function GalleryLightbox({
  images,
  index,
  onClose,
  onNavigate,
}: {
  images: ResolvedImage[];
  index: number;
  onClose: () => void;
  onNavigate: (next: number) => void;
}) {
  const count = images.length;
  const image = images[index];

  const goPrevious = useCallback(
    () => onNavigate((index - 1 + count) % count),
    [count, index, onNavigate],
  );
  const goNext = useCallback(
    () => onNavigate((index + 1) % count),
    [count, index, onNavigate],
  );

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
      else if (event.key === "ArrowLeft" && count > 1) goPrevious();
      else if (event.key === "ArrowRight" && count > 1) goNext();
    };
    document.addEventListener("keydown", onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [count, goNext, goPrevious, onClose]);

  if (!image) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={image.caption || `Photo ${index + 1}`}
      data-gallery-lightbox=""
      className="fixed inset-0 flex flex-col items-center justify-center p-4"
      style={{ zIndex: 120, backgroundColor: "rgba(2, 6, 23, 0.92)" }}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <button
        type="button"
        aria-label="Fermer"
        onClick={onClose}
        className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/25"
      >
        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <path d="M6 6l12 12M18 6L6 18" />
        </svg>
      </button>

      {count > 1 && (
        <button
          type="button"
          aria-label="Photo précédente"
          onClick={goPrevious}
          className="absolute left-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/25 md:left-6"
        >
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
      )}

      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={image.src}
        alt={image.caption || `Photo ${index + 1}`}
        className="rounded-md object-contain"
        style={{ maxHeight: "80vh", maxWidth: "92vw" }}
      />

      {count > 1 && (
        <button
          type="button"
          aria-label="Photo suivante"
          onClick={goNext}
          className="absolute right-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/25 md:right-6"
        >
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 6l6 6-6 6" />
          </svg>
        </button>
      )}

      <div className="mt-4 max-w-3xl text-center">
        {image.caption && (
          <p className="text-sm leading-6 text-white/90 md:text-base">{image.caption}</p>
        )}
        {count > 1 && (
          <p className="mt-1 text-xs text-white/55">{`${index + 1} / ${count}`}</p>
        )}
      </div>
    </div>
  );
}

/** Gallery renderer — photo frames with figure legends and lightbox zoom. */
export function GalleryRenderer({ component }: { component: GalleryComponent }) {
  const { assetResolver } = useRendererRuntime();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const images: GalleryImage[] = Array.isArray(component.content?.images)
    ? component.content.images
    : [];

  // Resolve every src through the asset resolver and drop images with no
  // usable src (empty key, or resolver returned "").
  const resolved: ResolvedImage[] = images
    .map((image) => {
      const key = (image?.src ?? "").trim();
      const src = key ? (assetResolver ? assetResolver(key) : key) : "";
      return { src, caption: image?.caption?.trim() ?? "" };
    })
    .filter((image) => image.src.length > 0);

  if (resolved.length === 0) return null;

  return (
    <section className="gallery-block py-6 md:py-8 xl:py-10">
      <div
        data-gallery-columns={component.content?.columns ?? ""}
        className={`grid gap-5 sm:gap-6 ${columnsClass(component.content?.columns)}`}
      >
        {resolved.map((image, index) => (
          <figure
            key={`${image.src}-${index}`}
            className="group relative overflow-hidden rounded-lg bg-slate-100 ring-1 ring-slate-200"
          >
            <button
              type="button"
              aria-label={`Agrandir la photo : ${image.caption || `Photo ${index + 1}`}`}
              onClick={() => setOpenIndex(index)}
              className="relative block w-full cursor-zoom-in"
            >
              <div className="aspect-[4/3] w-full">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={image.src}
                  alt={image.caption || `Photo ${index + 1}`}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                />
              </div>
              <span
                aria-hidden="true"
                className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-slate-950/55 text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100"
              >
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="7" />
                  <path d="M21 21l-4.3-4.3M11 8v6M8 11h6" />
                </svg>
              </span>
            </button>
            {image.caption && (
              <figcaption className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950/80 via-slate-950/40 to-transparent px-4 pb-3 pt-12 text-sm font-medium leading-5 text-white">
                {image.caption}
              </figcaption>
            )}
          </figure>
        ))}
      </div>
      {openIndex !== null && (
        <GalleryLightbox
          images={resolved}
          index={openIndex}
          onClose={() => setOpenIndex(null)}
          onNavigate={setOpenIndex}
        />
      )}
    </section>
  );
}
